<script>
    /* States */
    import {
        explorerState
    } from '@app/store/explorerStore.svelte.js';
    import { setExternalDataState } from "@app/store/externalDataStore.svelte.js";
    import { authState } from "@app/store/authStore.svelte.js";
    import { projectState, updateProjectId } from "@app/store/projectStore.svelte.js";
    import { metadataState } from "@app/store/metadataStore.svelte.js";
    import { speakerState } from "@app/store/speakerStore.svelte.js";
    import { issueState } from "@app/store/issueStore.svelte.js";
    import { markerState } from "@app/store/markerStore.svelte.js";
    import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
    import { styleState } from "@app/store/styleStore.svelte.js";
    import { Circle } from "svelte-loading-spinners";
    import { toast } from "@zerodevx/svelte-toast";
    import { showModal, hideModal } from "@app/store/modalStore.svelte.js";
    import {
        resetHistory, insertHistoryAction
    } from '@app/store/historyStore.svelte.js';
    import Swal from "sweetalert2";
    import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
    import { saveLastExplorerLocationFromRecord } from "@app/lib/explorerLastLocation.js";
    import { ccprjFunc } from '@app/lib/ccprj.js';
    import * as m from "@app/paraglide/messages.js";
    
    /* Firebase */
    import firebase from "@app/configs/firebase.js";
    import db from "@app/configs/firestore.js";
    import storage from "@app/configs/storage.js";;
    import "firebase/functions";

    const iconMap = {
        "folder" : "bi-folder-fill",
        "project" : "bi-file-earmark-play",
        "team project" : "bi-people",
        "work order" : "bi-clipboard-check"
    }
    const taskRecordTypes = ["project", "team project", "work order"];

    let userId = firebase.auth().currentUser.uid;
    let userEmail = firebase.auth().currentUser.email;
    let teamId = authState.team ? authState.team.id : null;
    let homeDbRef = db.collection("users").doc(userId).collection("storage");
    let teamDbRef = teamId ? db.collection("teams").doc(teamId).collection("storage") : null;
    let storageRef = storage.ref();
    let homeStorageBasePath = `users/${userId}/projects/`;
    let teamStorageBasePath = "teams/" + teamId + "/storage/";
    let operationLoading = $state(false); // For specific operations like overwrite, move
    let currentDragTarget = null;
    let sortKey = $state(/** @type {'priority' | 'dueDate' | null} */ (null));
    let sortDir = $state(/** @type {'asc' | 'desc'} */ ("desc"));

    let sortedRecords = $derived.by(() => {
        if (!sortKey) return explorerState.records;

        const records = [...explorerState.records];
        const direction = sortDir === "asc" ? 1 : -1;

        records.sort((a, b) => {
            if (sortKey === "priority") {
                const aVal = a.priority != null ? Number(a.priority) : null;
                const bVal = b.priority != null ? Number(b.priority) : null;
                if (aVal == null && bVal == null) return 0;
                if (aVal == null) return 1;
                if (bVal == null) return -1;
                return (aVal - bVal) * direction;
            }

            const aMillis = a.dueDate && typeof a.dueDate.toMillis === "function" ? a.dueDate.toMillis() : null;
            const bMillis = b.dueDate && typeof b.dueDate.toMillis === "function" ? b.dueDate.toMillis() : null;
            if (aMillis == null && bMillis == null) return 0;
            if (aMillis == null) return 1;
            if (bMillis == null) return -1;
            return (aMillis - bMillis) * direction;
        });

        return records;
    });

    function toggleSort(key) {
        if (sortKey === key) {
            sortDir = sortDir === "asc" ? "desc" : "asc";
        } else {
            sortKey = key;
            sortDir = key === "priority" ? "desc" : "asc";
        }
    }

    function sortCaret(key) {
        if (sortKey !== key) return "";
        return sortDir === "asc" ? " bi-caret-up-fill" : " bi-caret-down-fill";
    }
    
    // Helper function to check if a folder should be visible to current user based on group permissions
    function canUserAccessFolder(folder) {
        // Admin and Manager roles always have access to all folders
        if (authState.user && (authState.user.role === "admin" || authState.user.role === "manager")) {
            return true;
        }
        
        // If folder has no groups assigned, it's accessible to all team members
        if (!folder.groups || folder.groups.length === 0) {
            return true;
        }
        
        // Check if user is a member of any of the folder's assigned groups
        if (authState.user && authState.user.groups) {
            return folder.groups.some(groupId => authState.user.groups.includes(groupId));
        }
        
        // If user has no groups, they can't access group-restricted folders
        return false;
    }

    // Helper function to check if a project record should be visible based on its parent folder permissions
    async function canUserAccessRecord(record) {
        // Personal records are always accessible
        if (record.rootDir === "personal") {
            return true;
        }
        
        // If record has no locationId, it's at root level and accessible
        if (!record.locationId) {
            return true;
        }
        
        // For team records with locationId, check parent folder permissions
        if (record.rootDir === "team" && teamDbRef) {
            try {
                const parentFolderDoc = await teamDbRef.doc(record.locationId).get();
                
                // If parent folder doesn't exist, don't show the record
                if (!parentFolderDoc.exists) {
                    return false;
                }
                
                const parentFolder = parentFolderDoc.data();
                return canUserAccessFolder(parentFolder);
            } catch (error) {
                console.error("Error checking parent folder permissions:", error);
                return false;
            }
        }
        
        return true;
    }

    /* Get records where folder Id is the records locationId */
    async function getRecords(folderId = null){
        console.log("Calling get Records");
        try {
            explorerState.records = [];
            explorerState.selectedRecords = [];

            // console.log("Getting Records for Folder ID: " + folderId);
            
            // Handle special rootDir values
            if (explorerState.rootDir === "recent") {
                // Fetch recent records from both personal and team storage
                let recentRecords = [];
                
                // Personal storage
                let personalSnapshot = await homeDbRef
                    .orderBy("updatedOn", "desc")
                    .limit(100)
                    .get();
                    
                personalSnapshot.forEach((doc) => {
                    recentRecords.push({...(doc.data()), rootDir: "personal"});
                });
                
                // Team storage (if available)
                if (teamDbRef) {
                    let teamSnapshot = await teamDbRef
                        .orderBy("updatedOn", "desc")
                        .limit(100)
                        .get();
                        
                    teamSnapshot.forEach((doc) => {
                        recentRecords.push({...(doc.data()), rootDir: "team"});
                    });
                }
                
                // console.log("Before sorting:", recentRecords.map(r => ({ name: r.name, type: r.type })));
                
                // First, separate folders from other records
                const folders = recentRecords.filter(record => record.type === "folder");
                const nonFolders = recentRecords.filter(record => record.type !== "folder");
                
                // Sort each group by updatedOn timestamp
                folders.sort((a, b) => {
                    // Convert to milliseconds for comparison
                    const timeA = a.updatedOn ? a.updatedOn.toMillis() : 0;
                    const timeB = b.updatedOn ? b.updatedOn.toMillis() : 0;
                    return timeB - timeA; // Newest first
                });
                
                nonFolders.sort((a, b) => {
                    // Convert to milliseconds for comparison
                    const timeA = a.updatedOn ? a.updatedOn.toMillis() : 0;
                    const timeB = b.updatedOn ? b.updatedOn.toMillis() : 0;
                    return timeB - timeA; // Newest first
                });
                
                // Combine: folders first, then other records
                // Filter team folders by permissions
                const filteredFolders = folders.filter(folder => {
                    // If personal folder, keep it
                    if (folder.rootDir === "personal") {
                        return true;
                    }
                    // If team folder, check permissions
                    else if (folder.rootDir === "team") {
                        return canUserAccessFolder(folder);
                    }
                    return false;
                });
                
                // Filter non-folders (projects) by checking parent folder permissions
                const filteredNonFolders = [];
                for (const record of nonFolders) {
                    if (await canUserAccessRecord(record)) {
                        filteredNonFolders.push(record);
                    }
                }

                explorerState.records = [...filteredFolders, ...filteredNonFolders].slice(0, 100);
                
                // console.log("After sorting - folders:", folders.length, "non-folders:", nonFolders.length);
                // console.log("----RECENT RECORDS (WITH FOLDERS FIRST)----");
                // console.log(explorerState.records.map(r => ({ name: r.name, type: r.type })));
                
            } else if (explorerState.rootDir === "starred") {
                // Fetch starred records from both personal and team storage
                let starredRecords = [];
                
                // Personal storage
                let personalSnapshot = await homeDbRef
                    .where("star", "==", true)
                    .limit(100)
                    .get();
                    
                personalSnapshot.forEach((doc) => {
                    starredRecords.push({...(doc.data()), rootDir: "personal"});
                });
                
                // Team storage (if available)
                if (teamDbRef) {
                    let teamSnapshot = await teamDbRef
                        .where("star", "==", true)
                        .limit(100)
                        .get();
                        
                    teamSnapshot.forEach((doc) => {
                        starredRecords.push({...(doc.data()), rootDir: "team"});
                    });
                }
                
                // console.log("Before sorting:", starredRecords.map(r => ({ name: r.name, type: r.type })));
                
                // First, separate folders from other records
                const folders = starredRecords.filter(record => record.type === "folder");
                const nonFolders = starredRecords.filter(record => record.type !== "folder");
                
                // Sort each group by updatedOn timestamp
                folders.sort((a, b) => {
                    // Convert to milliseconds for comparison
                    const timeA = a.updatedOn ? a.updatedOn.toMillis() : 0;
                    const timeB = b.updatedOn ? b.updatedOn.toMillis() : 0;
                    return timeB - timeA; // Newest first
                });
                
                nonFolders.sort((a, b) => {
                    // Convert to milliseconds for comparison
                    const timeA = a.updatedOn ? a.updatedOn.toMillis() : 0;
                    const timeB = b.updatedOn ? b.updatedOn.toMillis() : 0;
                    return timeB - timeA; // Newest first
                });
                
                // Combine: folders first, then other records
                // Filter team folders by permissions
                const filteredFolders = folders.filter(folder => {
                    // If personal folder, keep it
                    if (folder.rootDir === "personal") {
                        return true;
                    }
                    // If team folder, check permissions
                    else if (folder.rootDir === "team") {
                        return canUserAccessFolder(folder);
                    }
                    return false;
                });
                
                // Filter non-folders (projects) by checking parent folder permissions
                const filteredNonFolders = [];
                for (const record of nonFolders) {
                    if (await canUserAccessRecord(record)) {
                        filteredNonFolders.push(record);
                    }
                }

                explorerState.records = [...filteredFolders, ...filteredNonFolders].slice(0, 100);
                
                // console.log("After sorting - folders:", folders.length, "non-folders:", nonFolders.length);
                // console.log("----STARRED RECORDS (WITH FOLDERS FIRST)----");
                // console.log(explorerState.records.map(r => ({ name: r.name, type: r.type })));
                
            } else if (explorerState.rootDir === "tasks") {
                let taskRecords = [];

                let personalSnapshot = await homeDbRef
                    .where("assignedTo", "==", userEmail)
                    .limit(100)
                    .get();

                personalSnapshot.forEach((doc) => {
                    const record = doc.data();
                    const recordId = record.id || doc.id;
                    taskRecords.push({
                        ...record,
                        id: recordId,
                        rootDir: "personal",
                        uniqueId: `personal-${recordId}`
                    });
                });

                if (teamDbRef) {
                    let teamSnapshot = await teamDbRef
                        .where("assignedTo", "==", userEmail)
                        .limit(100)
                        .get();

                    teamSnapshot.forEach((doc) => {
                        const record = doc.data();
                        const recordId = record.id || doc.id;
                        taskRecords.push({
                            ...record,
                            id: recordId,
                            rootDir: "team",
                            uniqueId: `team-${recordId}`
                        });
                    });
                }

                taskRecords = taskRecords.filter(record => taskRecordTypes.includes(record.type) && record.state !== "Archived");

                const filteredRecords = [];
                for (const record of taskRecords) {
                    if (await canUserAccessRecord(record)) {
                        filteredRecords.push(record);
                    }
                }

                filteredRecords.sort((a, b) => {
                    const timeA = a.updatedOn ? a.updatedOn.toMillis() : 0;
                    const timeB = b.updatedOn ? b.updatedOn.toMillis() : 0;
                    return timeB - timeA;
                });

                explorerState.records = filteredRecords.slice(0, 100);

            } else {
                // Normal folder query for personal or team storage
                let querySnapshot = await (explorerState.rootDir === "personal" ? homeDbRef : teamDbRef).where("locationId", "==", folderId).orderBy("type").orderBy("name", "asc").get();

                querySnapshot.forEach((doc) => {
                    const record = {...(doc.data()), rootDir: explorerState.rootDir};
                    
                    // For personal storage, add all records
                    if (explorerState.rootDir === "personal") {
                        explorerState.records = [...explorerState.records, record];
                    }

                    // For team storage, filter folders by permissions
                    else if (explorerState.rootDir === "team") {
                        // If it's a folder, check permissions
                        console.log("Checking access for folder:", record);
                        if (record.type === "folder") {
                            if (canUserAccessFolder(record)) {
                                explorerState.records = [...explorerState.records, record];
                            }
                        } 
                        // Non-folder records are always shown (they get filtered at the folder level)
                        else {
                            explorerState.records = [...explorerState.records, record];
                        }
                    }
                });

                // console.log("----RECORDS----");
                // console.log(explorerState.records);
            }
        } catch(err){
            console.error(err);
            toast.push(err.message, {
                classes: ["toast-danger"],
            });
        }   
    }

    async function openRecord(id) {
        // console.log("Opening record with id: ", id);
        let record = explorerState.records.find(record => record.id === id);
        // console.log("----Record----");
        // console.log(record);
        // console.log("--------------");

        // Handle folder navigation
        if (record.type === "folder") {
            // console.log(`Opening folder: ${record.name}, current rootDir: ${explorerState.rootDir}`);
            
            // Check if we're in a system result view
            if (explorerState.rootDir === "recent" || explorerState.rootDir === "starred" || explorerState.rootDir === "tasks") {
                // console.log(`Switching from ${explorerState.rootDir} view to ${record.rootDir} view`);
                
                // Switch to the appropriate rootDir based on the folder's rootDir
                explorerState.rootDir = record.rootDir;
                
                // console.log(`Building navigation path for folder: ${record.name}`);
                // Build the navigation path by finding all parent folders
                await buildFolderNavigationPath(record);
                
                // console.log(`Navigation path built, length: ${explorerState.navHistory.length}`);
            } else {
                // console.log(`Regular folder navigation in ${explorerState.rootDir} view`);
                // Regular folder navigation in normal view
                explorerState.navHistory = [...explorerState.navHistory, record];
            }
            
            // console.log(`Getting records for folder: ${record.name}, id: ${record.id}`);
            // Navigate to the folder contents
            getRecords(record.id);
        } 
        // Handle projects differently based on context
        else if (record.type === "project" || record.type === "team project" || record.type === "work order") {
            // If in save mode, overwrite the project instead of opening it
            if (explorerState.context === "save") {
                overwriteProject(record);
            } else {
                // In load mode, open the project as before
                loadProject(record);
            }
        } 
    }
    
    // Build navigation path for a folder by finding all parent folders
    async function buildFolderNavigationPath(folder) {
        try {
            operationLoading = true;
            // console.log(`Building navigation path for folder: ${folder.name} (ID: ${folder.id})`);
            
            // Clear current navigation history
            explorerState.navHistory = [];
            
            // If no folder, we're at root
            if (!folder) {
                // console.log("No folder provided, returning empty path");
                return;
            }
            
            // Select the appropriate database reference
            const dbRef = folder.rootDir === "personal" ? homeDbRef : teamDbRef;
            if (!dbRef) {
                throw new Error(`Invalid database reference for rootDir: ${folder.rootDir}`);
            }
            
            // console.log(`Using ${folder.rootDir} database reference`);
            
            // First add the current folder to the path
            let folderPath = [];
            
            // If folder has no locationId, it's at root level already
            if (!folder.locationId) {
                // console.log(`Folder is at root level: ${folder.name}`);
                explorerState.navHistory = [folder];
                return;
            }
            
            // Start with the current folder's locationId to find its parent
            let currentFolderId = folder.locationId;
            let pathComplete = false;
            let safetyCounter = 0; // Prevent infinite loops
            const MAX_DEPTH = 20; // Maximum folder nesting depth to prevent infinite loops
            
            // Keep traversing up until we reach the root or hit safety limit
            while (currentFolderId && !pathComplete && safetyCounter < MAX_DEPTH) {
                safetyCounter++;
                // console.log(`Looking up parent folder with ID: ${currentFolderId}`);
                
                try {
                    // Get the parent folder
                    const parentDoc = await dbRef.doc(currentFolderId).get();
                    
                    if (parentDoc.exists) {
                        const parentData = parentDoc.data();
                        const parentFolder = { ...parentData, rootDir: folder.rootDir };
                        
                        // console.log(`Found parent: ${parentFolder.name} (ID: ${parentFolder.id})`);
                        
                        // Add to path (at the beginning)
                        folderPath.unshift(parentFolder);
                        
                        // Continue with parent's parent if it exists
                        if (parentFolder.locationId) {
                            currentFolderId = parentFolder.locationId;
                        } else {
                            // console.log("Reached root folder in hierarchy");
                            pathComplete = true;
                        }
                    } else {
                        // console.log(`Parent folder not found with ID: ${currentFolderId}`);
                        pathComplete = true;
                    }
                } catch (err) {
                    console.error(`Error fetching parent folder: ${err.message}`);
                    pathComplete = true;
                }
            }
            
            if (safetyCounter >= MAX_DEPTH) {
                console.warn("Possible circular reference in folder structure, path building stopped");
            }
            
            // Add the original folder at the end
            folderPath.push(folder);
            
            // console.log("Full navigation path built:", folderPath);
            
            // Update navigation history with the complete path
            explorerState.navHistory = folderPath;
            
        } catch (error) {
            console.error("Error building folder navigation path:", error);
            toast.push(`Failed to build folder path: ${error.message}`, {
                classes: ["toast-danger"],
            });
        } finally {
            operationLoading = false;
        }
    }
    
    async function overwriteProject(record) {
        if (operationLoading) return;
        
        try {
            operationLoading = true;
            
            // Check if record is locked by someone else
            if (record.locked && record.lockedBy !== userEmail) {
                throw new Error(m.explorer_table_overwrite_locked_error({ name: record.name, lockedBy: record.lockedBy }));
            }
            
            // Show confirmation dialog
            const result = await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.destructiveDeny,
                titleText: m.explorer_table_overwrite_title(),
                text: m.explorer_table_overwrite_text({ name: record.name }),
                showDenyButton: true,
                showCancelButton: false,
                confirmButtonText: m.explorer_table_overwrite_confirm(),
                denyButtonText: m.explorer_table_cancel_button(),
                allowOutsideClick: false,
                allowEscapeKey: false,
            });
            
            if (!result.isConfirmed) {
                throw new Error(m.explorer_table_overwrite_cancelled_error());
            }
            
            // Update the project to use the same ID as the selected record
            updateProjectId(record.id);
            
            // Determine file path based on storage location
            const projectFilePath = record.rootDir === "team"
                ? teamStorageBasePath + record.id
                : homeStorageBasePath + record.id;
                
            // Create project JSON
            const projectJson = ccprjFunc.createCcprj({
                project: JSON.parse(JSON.stringify(projectState)),
                metadata: JSON.parse(JSON.stringify(metadataState)),
                speakers: JSON.parse(JSON.stringify(speakerState)),
                issues: JSON.parse(JSON.stringify(issueState)),
                markers: JSON.parse(JSON.stringify(markerState)),
                eventGroups: JSON.parse(JSON.stringify(eventGroupState)),
                style: JSON.parse(JSON.stringify(styleState))
            });
            
            // Upload to storage
            const uploadProjectRef = storage.ref().child(projectFilePath);
            await uploadProjectRef.putString(JSON.stringify(projectJson));
            
            // Update project record in database
            const projectInfo = {
                id: record.id,
                name: projectState.name,
                type: projectState.type === "team" ? "team project" : "project",
                owner: userEmail,
                createdOn: record.createdOn,
                updatedOn: firebase.firestore.Timestamp.fromDate(new Date()),
                locationId: record.locationId,
                shared: record.rootDir === "team" ? true : false,
                star: record.star || false,
                description: record.description || "",
                rootDir: record.rootDir,
                assignedTo: record.assignedTo ?? null,
                state: record.state ?? null,
                locked: record.locked || false,
                lockedBy: record.lockedBy ?? null,
                lockedOn: record.lockedOn ?? null,
                dueDate: record.dueDate ?? null,
                priority: record.priority ?? null,
                totalEvents: record.totalEvents ?? null,
                eventGroups: record.eventGroups ?? null,
            };
            
            const dbRef = record.rootDir === "team" ? teamDbRef : homeDbRef;
            await dbRef.doc(projectInfo.id).set(projectInfo);
            
            // Update project state with location info
            projectState.folderId = record.locationId;
            projectState.rootDir = record.rootDir;
            
            // Reset history and show success message
            resetHistory();
            insertHistoryAction({
                name: "save project", //action name
                eventGroup: projectState.selected,
                snapshots: [
                    {
                        store: "eventGroupState",
                        value: JSON.stringify(eventGroupState),
                    }
                ],
            });
            toast.push(m.explorer_table_overwrite_success_toast({ name: projectState.name }), {
                classes: ["toast-success"],
            });
            
            // Update the record in the records list
            explorerState.records = explorerState.records.map(r => 
                r.id === record.id ? projectInfo : r
            );

            saveLastExplorerLocationFromRecord(userId, record);
            
            hideModal();
        } catch (error) {
            console.error("Error overwriting project:", error);
            toast.push(m.explorer_table_overwrite_failed_toast({ error: error.message }), {
                classes: ["toast-danger"],
            });
        } finally {
            operationLoading = false;
        }
    }

    async function loadProject(record) {
        try {
            operationLoading = true;
            
            // Check if project is locked by someone else
            if (record.locked && record.lockedBy !== userEmail) {
                // Show notification about locked project
                await Swal.fire({
                    ...SWAL_BASE,
                    customClass: SWAL_CLASSES.ok,
                    title: m.explorer_table_locked_title(),
                    text: m.explorer_table_locked_text({ lockedBy: record.lockedBy }),
                    icon: 'info',
                    confirmButtonText: m.explorer_table_locked_confirm(),
                });
            }
            
            // Determine the storage path based on whether it's personal or team
            const projectFilePath = record.rootDir === "team" 
                ? `${teamStorageBasePath}${record.id}` 
                : `${homeStorageBasePath}${record.id}`;
            
            // Get the URL for the file
            let url;
            console.log("Getting Project File URL: " + projectFilePath);
            if (record.rootDir === "team") {
                const result = await firebase.functions().httpsCallable("v8GetDownloadLink")(projectFilePath);
                url = result.data;
            } else {
                url = await storageRef.child(projectFilePath).getDownloadURL();
            }

            console.log("Project File URL: ", url);
            
            // Fetch the file content
            const response = await fetch(url);
            const fileData = await response.json();
            fileData.rootDir = record.rootDir;

            console.log("Project File Data: ", fileData);
            
            // Process based on record type
            if (record.type === "work order") {
                openWorkOrder(fileData, record);
            } else if (record.type === "project") {
                openProjectFile(fileData, record);
            } else if (record.type === "team project") {
                openTeamProjectFile(fileData, record);
            }
            
        } catch (error) {
            console.error(`Error opening ${record.type}:`, error);
            toast.push(m.explorer_table_open_failed_toast({ type: record.type, error: error.message }), {
                classes: ["toast-danger"],
            });
        } finally {
            operationLoading = false;
        }
    }

    function openProjectFile(description, record){
        console.log("Opening project file with description: ", description);
        // Add folderId to description object
        description.folderId = record.locationId;

        saveLastExplorerLocationFromRecord(userId, record);
        
        setExternalDataState({
            platform: "Project File",
            status : "importing",
            rawDescription: JSON.stringify(description)
        });

        showModal("loadProjectFile");
    }

    async function openTeamProjectFile(description, selectedRecord) {
        try {
            operationLoading = true;
            // Extract team ID and project ID
            const projectTeamId = description.teamId;
            
            if (!projectTeamId || !teamId) {
                throw new Error("Missing team or project ID in file");
            }
            
            // console.log(`Looking up share codes for team project: ${projectTeamId} in team: ${teamId}`);
            
            // Check for project snapshots first (to ensure there's data to share)
            const snapshotsRef = db.collection("teams").doc(teamId)
                .collection("projects").doc(projectTeamId)
                .collection("snapshots");
                
            const snapshotsQuery = await snapshotsRef.limit(1).get();
            
            if (snapshotsQuery.empty) {
                // console.log("No snapshots found for team project, falling back to project file import");
                fallbackToProjectFileImport(description, selectedRecord);
                return;
            }
            
            // Look for share codes that reference this project
            const sharesRef = db.collection("teams").doc(teamId).collection("shares");
                
            const sharesQuery = await sharesRef
                .where("projectTeamId", "==", projectTeamId)
                .limit(1)
                .get();            
            
            if (sharesQuery.empty) {
                // console.log("No share codes found for team project, falling back to project file import");
                fallbackToProjectFileImport(description, selectedRecord);
                return;
            }
            
            // Get the first share code
            const shareDoc = sharesQuery.docs[0];
            const shareId = shareDoc.id;
            
            // console.log(`Found share code: ${shareId}`);
            
            // Update URL with share code and open teamProjectImport modal
            const url = new URL(window.location.href);
            url.searchParams.set("id", shareId);
            window.history.pushState({}, '', url);
            
            toast.push(m.explorer_table_open_team_project_toast(), {
                classes: ["toast-info"],
            });

            saveLastExplorerLocationFromRecord(userId, selectedRecord);
            
            showModal("teamProjectImport");
            
        } catch (error) {
            console.error("Error processing team project:", error);
            fallbackToProjectFileImport(description, selectedRecord);
        }
    }
    
    function fallbackToProjectFileImport(description, record) {
        console.log("Falling back to project file import method");
        toast.push(m.explorer_table_fallback_project_import_toast(), {
            classes: ["toast-info"],
        });
        
        // Add folderId to description object
        description.folderId = record.locationId;

        saveLastExplorerLocationFromRecord(userId, record);
        
        setExternalDataState({
            platform: "Project File",
            status : "importing",
            rawDescription: JSON.stringify(description)
        });
        
        showModal("loadProjectFile");
    }

    function openWorkOrder(description, record) {
        // Add folderId to description object
        description.folderId = record.locationId;

        saveLastExplorerLocationFromRecord(userId, record);
        
        setExternalDataState({
            platform: "Work Order",
            status : "importing",
            rawDescription: JSON.stringify(description)
        });
        
        //call workOrderImport modal
        showModal("workOrderImport");
    }

    function selectRecord(clickEvent, id) {
        // console.log("Selecting record with id: ", id);
        if (clickEvent.ctrlKey || clickEvent.metaKey) {
            if (explorerState.selectedRecords.includes(id)) {
                explorerState.selectedRecords = explorerState.selectedRecords.filter(recordId => recordId !== id);
            } else {
                explorerState.selectedRecords = [...explorerState.selectedRecords, id];
            }
        } else if (clickEvent.shiftKey) {
            const lastSelectedRecord = explorerState.selectedRecords[explorerState.selectedRecords.length - 1];
            const lastSelectedRecordIndex = explorerState.records.findIndex(record => record.id === lastSelectedRecord);
            const currentRecordIndex = explorerState.records.findIndex(record => record.id === id);
            const recordsToSelect = explorerState.records.slice(Math.min(lastSelectedRecordIndex, currentRecordIndex), Math.max(lastSelectedRecordIndex, currentRecordIndex) + 1);
            explorerState.selectedRecords = recordsToSelect.map(record => record.id);
        } else {
            explorerState.selectedRecords = [id];
        }
    }

    // Drag and drop functions
    function handleDragStart(event, id) {
        // Don't allow drag in save mode
        if (explorerState.context === "save") {
            event.preventDefault();
            return;
        }
        
        // If the current item isn't selected, select only this item
        if (!explorerState.selectedRecords.includes(id)) {
            explorerState.selectedRecords = [id];
        }
        
        // Set drag data
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", JSON.stringify(explorerState.selectedRecords));
        
        // Set a custom class for styling
        const draggedItems = explorerState.selectedRecords.length;
        event.dataTransfer.setData("application/count", draggedItems);
        event.target.classList.add("dragging");
    }
    
    function handleDragEnd(event) {
        event.target.classList.remove("dragging");
        currentDragTarget = null;
    }
    
    function handleDragOver(event, record) {
        // Only allow dropping on folders
        if (record.type !== "folder") {
            return;
        }
        
        // Get the dragged item IDs
        const dragDataString = event.dataTransfer.types.includes('text/plain') ? 
            event.dataTransfer.getData('text/plain') : null;
            
        // If we're dragging the folder onto itself, don't allow drop
        if (dragDataString) {
            try {
                const draggedIds = JSON.parse(dragDataString);
                // Prevent dropping onto self
                if (draggedIds.includes(record.id)) {
                    event.dataTransfer.dropEffect = "none";
                    return;
                }
            } catch (e) {
                // If parsing fails, continue with normal behavior
            }
        }
        
        // Prevent default to allow drop
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
        
        // Highlight the drop target
        if (currentDragTarget && currentDragTarget !== record.id) {
            const prevElement = document.querySelector(`[data-record-id="${currentDragTarget}"]`);
            if (prevElement) prevElement.classList.remove("drop-target");
        }
        
        event.currentTarget.classList.add("drop-target");
        currentDragTarget = record.id;
    }
    
    function handleDragLeave(event) {
        event.currentTarget.classList.remove("drop-target");
    }
    
    async function handleDrop(event, targetRecord) {
        event.preventDefault();
        event.currentTarget.classList.remove("drop-target");
        
        // Only allow dropping on folders
        if (targetRecord.type !== "folder") {
            return;
        }
        
        try {
            // Get the dragged record IDs
            const recordIds = JSON.parse(event.dataTransfer.getData("text/plain"));
            
            // Don't move if nothing was dragged or trying to move to same folder
            if (!recordIds.length) return;
            
            // Check if trying to drop a folder onto itself
            if (recordIds.includes(targetRecord.id)) {
                toast.push(m.explorer_table_cannot_move_into_self_toast(), {
                    classes: ["toast-warning"],
                });
                return;
            }
            
            const recordsToMove = explorerState.records.filter(r => recordIds.includes(r.id));
            
            // Check if any of the records are already in the target folder
            const alreadyInFolder = recordsToMove.some(r => r.locationId === targetRecord.id);
            if (alreadyInFolder) {
                toast.push(m.explorer_table_already_in_folder_toast(), {
                    classes: ["toast-warning"],
                });
                return;
            }
            
            // Confirm move with the user
            await moveRecords(recordsToMove, targetRecord);
            
        } catch (error) {
            console.error("Error handling drop:", error);
            toast.push(m.explorer_table_drop_failed_toast({ error: error.message }), {
                classes: ["toast-danger"],
            });
        }
    }
    
    async function moveRecords(records, targetFolder) {
        if (operationLoading) return;
        
        try {
            // Show confirmation dialog
            const itemSuffix = records.length > 1 ? "s" : "";
            const result = await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.denyCancel,
                titleText: m.explorer_table_move_title(),
                text: m.explorer_table_move_text({
                    count: records.length,
                    suffix: itemSuffix,
                    name: targetFolder.name,
                }),
                showDenyButton: true,
                confirmButtonText: m.explorer_table_move_confirm(),
                denyButtonText: m.explorer_table_cancel_button(),
                icon: "question",
            });
            
            if (!result.isConfirmed) return;
            
            operationLoading = true;
            
            // Check if any records are locked
            const lockedRecords = records.filter(r => r.locked && r.lockedBy !== userEmail);
            if (lockedRecords.length > 0) {
                const lockedSuffix = lockedRecords.length > 1 ? "s" : "";
                throw new Error(m.explorer_table_move_locked_error({ suffix: lockedSuffix }));
            }
            
            // Perform the move operation for each record
            for (const record of records) {
                // Get the appropriate database reference based on the root directory
                const dbRef = record.rootDir === "team" ? teamDbRef : homeDbRef;
                
                // Update the locationId
                await dbRef.doc(record.id).update({
                    locationId: targetFolder.id,
                    updatedOn: firebase.firestore.Timestamp.fromDate(new Date())
                });
                
                // Update local record
                const index = explorerState.records.findIndex(r => r.id === record.id);
                if (index !== -1) {
                    explorerState.records[index].locationId = targetFolder.id;
                }
            }
            
            // Refresh the records list
            await getRecords(explorerState.navHistory[explorerState.navHistory.length - 1]?.id || null);
            
            // Clear selected records
            explorerState.selectedRecords = [];
            
            toast.push(m.explorer_table_move_success_toast({
                count: records.length,
                suffix: itemSuffix,
                name: targetFolder.name,
            }), {
                classes: ["toast-success"],
            });
            
        } catch (error) {
            console.error("Error moving records:", error);
            toast.push(m.explorer_table_move_failed_toast({ error: error.message }), {
                classes: ["toast-danger"],
            });
        } finally {
            operationLoading = false;
        }
    }
</script>
{#if explorerState.loading}
<div class="loading-container d-flex justify-content-center align-items-center w-100 h-100">
    <Circle
    size="75"
    color="#1eb4b2"
    unit="px"
    duration="1s"
    ></Circle>
</div>
{:else}
    <div id="workspace-table-view" class="table-responsive border rounded border-0 my-2">
        <table class="table table-hover table-light border-secondary">
            <thead class="bg-light">
                <tr>
                    <th class="col-icon"><!-- Lock Column --></th>
                    <th class="col-icon"><!-- Star Column --></th>
                    <th class="col-name">{m.explorer_table_name_header()}</th>
                    <th class="col-state">{m.explorer_table_state_header()}</th>
                    <th class="col-sm">{m.explorer_table_owner_header()}</th>
                    <th class="col-sm">{m.explorer_table_assigned_to_header()}</th>
                    <th class="col-sm">
                        <button
                            type="button"
                            class="btn btn-link btn-sm text-decoration-none text-body p-0 fw-bold"
                            aria-label={m.explorer_table_sort_by_priority_aria()}
                            onclick={() => toggleSort("priority")}
                        >
                            {m.explorer_table_priority_header()}
                            <i class="bi{sortCaret('priority')}"></i>
                        </button>
                    </th>
                    <th class="col-sm">
                        <button
                            type="button"
                            class="btn btn-link btn-sm text-decoration-none text-body p-0 fw-bold"
                            aria-label={m.explorer_table_sort_by_due_date_aria()}
                            onclick={() => toggleSort("dueDate")}
                        >
                            {m.explorer_table_due_date_header()}
                            <i class="bi{sortCaret('dueDate')}"></i>
                        </button>
                    </th>
                    <th class="col-sm">{m.explorer_table_event_groups_header()}</th>
                    <th class="col-sm">{m.explorer_table_total_events_header()}</th>
                    <th class="col-sm">{m.explorer_table_last_modified_header()}</th>
                    <th class="col-sm">{m.explorer_table_created_on_header()}</th>
                </tr>
            </thead>
            <tbody>
                {#each sortedRecords as record (record.uniqueId || record.id)}
                <tr 
                    ondblclick={() => openRecord(record.id)} 
                    onclick={(clickEvent) => selectRecord(clickEvent, record.id)} 
                    class={(explorerState.selectedRecords.includes(record.id) ? 'table-primary' : '')}
                    draggable={explorerState.context !== "save"}
                    ondragstart={(e) => handleDragStart(e, record.id)}
                    ondragend={handleDragEnd}
                    ondragover={(e) => handleDragOver(e, record)}
                    ondragleave={handleDragLeave}
                    ondrop={(e) => handleDrop(e, record)}
                    data-record-id={record.id}
                    data-record-type={record.type}>
                    <td>{#if record.locked}<i class="bi bi-lock-fill"></i>{/if}</td>
                    <td>{#if record.star}<i class="bi bi-star-fill"></i>{/if}</td>
                    <td><i class="bi {iconMap[record.type]}"></i> {record.name}</td>
                    <td>{record.state || "-"}</td>
                    <td>{record.owner || "-"}</td>
                    <td>{record.assignedTo || "-"}</td>
                    <td>{record.priority != null ? record.priority : "-"}</td>
                    <td>{record.dueDate ? record.dueDate.toDate().toLocaleDateString() : "-"}</td>
                    <td>{record.eventGroups || "-"}</td>
                    <td>{record.totalEvents || "-"}</td>
                    <td>{record.updatedOn.toDate().toLocaleDateString()}</td>
                    <td>{record.createdOn.toDate().toLocaleDateString()}</td>                
                </tr>
                {/each}
            </tbody>
        </table>
    </div>
{/if}
<style lang="css">
    #workspace-table-view {
        height: 80vh;
        overflow-y: auto;
    }
    
    .loading-container {
        height: 80vh;
    }
    
    /* Add sticky header styles */
    table {
        position: relative;
    }
    
    thead {
        position: sticky;
        top: 0;
        z-index: 1;
    }
    
    /* Drag and drop styles */
    tr[data-record-type="folder"] {
        cursor: pointer;
    }    
    
    /* Column width styles */
    .col-icon {
        width: 40px;
        min-width: 40px;
    }
    
    .col-name {
        width: 30%;
        min-width: 250px;
    }
    
    .col-state {
        width: 15%;
        min-width: 150px;
    }
    
    .col-sm {
        width: 9%;
        min-width: 120px;
    }
</style>